/**
 * static/src/analytics/ga4.js
 *
 * GA4 (Google Analytics 4) initialisation for leadranks.io.
 *
 * Why this is a bundled module and NOT Google's stock inline snippet
 * ------------------------------------------------------------------
 * Google's canonical install is two <script> tags in index.html — one async
 * loader for gtag.js, one INLINE bootstrap that defines `gtag()` and calls
 * `gtag('config', ...)`. This site's Content-Security-Policy (nginx.conf,
 * `script-src`) deliberately has no `'unsafe-inline'`, so that inline
 * bootstrap would be blocked outright. Rather than weaken the CSP (the whole
 * point of which is to prevent injected-script XSS), the bootstrap lives here,
 * same-origin, and is bundled by Vite like any other module. Only the external
 * gtag.js host needed a CSP allowance (script-src += googletagmanager.com).
 *
 * Page-view model (SPA)
 * ---------------------
 * This is a single-page app: the browser loads the document once, then Vue
 * Router swaps views. Sending a page_view on every route change — AND letting
 * `config` also fire its default one — would double-count the landing page.
 * So `send_page_view: false` here, and router/index.js's afterEach emits
 * exactly one `page_view` event per navigation (including the first).
 *
 * The measurement ID is a public data-stream tag (same class of value as a
 * plain `G-` tag on any website) — it identifies where hits are sent, it is
 * not a credential. It is intentionally specific to leadranks.io; do NOT reuse
 * an ID that belongs to another property, or both sites' traffic merges.
 *
 * DO NOT "simplify" this to the GA4 property ID (2026-09-12): Google has
 * two identifiers that look identical (G- + 10 chars) but do different
 * jobs, and it's genuinely easy to conflate them.
 *   - G-W2XM0FRRCT (this one) is a "Google tag" -- a loadable container
 *     meant to be pasted into a site. Requesting it from Google's server
 *     (https://www.googletagmanager.com/gtag/js?id=G-W2XM0FRRCT) returns
 *     real, working JavaScript.
 *   - G-V06E72NSQD is the underlying GA4 property/data-stream ID -- the
 *     one you'd look at in the GA4 dashboard. This tag's own container
 *     forwards everything it collects there (`vtp_instanceDestinationId`
 *     is G-V06E72NSQD throughout its config, confirmed directly against
 *     Google's server). It is NOT independently loadable: requesting
 *     gtag.js for G-V06E72NSQD returns a literal Google 404 page, not
 *     JavaScript -- confirmed the same way. Swapping this constant to
 *     that value would silently break analytics entirely (the <script>
 *     tag fails to load, gtag() never initializes, zero data reaches
 *     Google) -- a real mistake caught and reverted here before it
 *     shipped. There's no way to skip the wrapper for this property; use
 *     the ID below, not the dashboard's own property ID, no matter how
 *     tempting the "simplification" looks.
 *
 * NOT an env var: this is intentionally a hardcoded constant, not read
 * from .env.secrets or any Vite build-time env var. It's already public
 * (rendered into every visitor's page source), this frontend has no
 * existing env-var injection pipeline at all, and GA4 needs to initialise
 * synchronously before mount (see initGA4() below) -- an env var would add
 * indirection for a value with nothing left to protect.
 */

export const GA4_MEASUREMENT_ID = 'G-W2XM0FRRCT';

/**
 * Initialise GA4. Idempotent — safe to call more than once. Returns true when
 * the shim is in place (never throws: analytics must never break the app).
 */
export function initGA4(id = GA4_MEASUREMENT_ID) {
  if (typeof window === 'undefined' || !id) return false;
  if (window.__ga4Ready) return true;

  try {
    window.__ga4Ready = true;

    // Google's own gtag() shim, in our bundle instead of an inline <script>.
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;

    gtag('js', new Date());
    // See module docstring: the router owns page_view events for this SPA.
    gtag('config', id, { send_page_view: false });

    // Load gtag.js once, async -- and DELIBERATELY LATE (2026-09-19).
    //
    // It used to be appended to <head> the moment this module evaluated,
    // which meant a 168 KiB third-party download, ~86 ms of main-thread
    // work and a 70 ms forced reflow from gtag.js all landed inside the
    // first-paint window. PageSpeed Insights flagged all three on the
    // mobile run (LCP 4.8s) -- none of it is needed before the hero paints.
    //
    // Analytics must not delay rendering, so the loader tag is now created
    // on window 'load' (or immediately if the document already finished).
    // This is safe: the gtag() shim and window.dataLayer above are real and
    // synchronous, so every event queued in the meantime -- including the
    // 'js'/'config' commands and the router's page_view -- is buffered and
    // replayed by gtag.js the moment it arrives. Nothing is lost; it is
    // only the network fetch that moves.
    const appendLoader = () => {
      if (document.querySelector('script[data-ga4-loader]')) return;
      const s = document.createElement('script');
      s.async = true;
      s.setAttribute('data-ga4-loader', '1');
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
      document.head.appendChild(s);
    };
    if (document.readyState === 'complete') {
      appendLoader();
    } else {
      window.addEventListener('load', appendLoader, { once: true });
    }

    return true;
  } catch (e) {
    // Never let analytics take the app down with it.
    // eslint-disable-next-line no-console
    console.warn('[ga4] init failed:', e && e.message ? e.message : e);
    return false;
  }
}
